import React from "react";
import { Grid, Card, CardContent, Typography, Box, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PeopleIcon from "@mui/icons-material/People";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import LocalParkingIcon from "@mui/icons-material/LocalParking";
import HandymanIcon from "@mui/icons-material/Handyman";
import BuildIcon from "@mui/icons-material/Build";
import BusinessIcon from "@mui/icons-material/Business";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import PersonIcon from "@mui/icons-material/Person";
import { formatCurrency } from "../../../utils/utils";
import { useToast } from "../../../components/Toast";
import { OperationType } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";

export interface PartyTypeConfig {
  title: string;
  icon: React.ReactElement;
  operationTypes?: OperationType[];
}

interface PartySummary {
  partyType: string;
  totalParties: number;
  totalTransactions: number;
  totalDue?: number;
  totalPaid?: number;
  totalBalance: number;
  totalSettlement?: number;
  // Income-specific fields
  totalBooking?: number;
  totalCollection?: number;
  totalAdvance?: number;
  netReceived?: number;
  netReceivable?: number;
}

interface PartySummaryCardsProps {
  summaryData: Record<string, PartySummary>;
  loading?: boolean;
  type?: "income" | "expense";
}

// Expense party types configuration
export const expensePartyTypeConfig: Record<string, PartyTypeConfig> = {
  staff: {
    title: "Staff Payroll",
    icon: <PeopleIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  fuel: {
    title: "Fuel Providers",
    icon: <LocalGasStationIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  parking: {
    title: "Parkings",
    icon: <LocalParkingIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  mechanic: {
    title: "Mechanics",
    icon: <HandymanIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  garage: {
    title: "Garages",
    icon: <BuildIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  "outsource-owner": {
    title: "Outsource Owners",
    icon: <PersonIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.SCHOOL,
      OperationType.LARGE_FLEET,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  "pickup-man": {
    title: "Pickup Man",
    icon: <PersonIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
};

// Income party types configuration
export const incomePartyTypeConfig: Record<string, PartyTypeConfig> = {
  "bus-operator": {
    title: "Bus Operators",
    icon: <BusinessIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  "luggage-operator": {
    title: "Luggage Operators",
    icon: <PeopleIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  "pickup-man": {
    title: "Pickup Man",
    icon: <DirectionsBusIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.DAILY_BUS,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  party: {
    title: "Customer Parties",
    icon: <PersonIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.TOUR_BOOKING,
      OperationType.CONTRACT,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
  client: {
    title: "Clients",
    icon: <BusinessIcon sx={{ fontSize: 20 }} />,
    operationTypes: [
      OperationType.CONTRACT,
      OperationType.LARGE_FLEET_ALL,
      OperationType.ALL,
    ],
  },
};

// Helper function to get filtered party categories for dropdowns
export const getFilteredPartyCategories = (
  type: "income" | "expense",
  currentOperationType: OperationType
) => {
  const config =
    type === "income" ? incomePartyTypeConfig : expensePartyTypeConfig;

  return Object.entries(config)
    .filter(([key, partyConfig]) => {
      // If no operationTypes defined, show for all operations (backward compatibility)
      if (
        !partyConfig.operationTypes ||
        partyConfig.operationTypes.length === 0
      ) {
        return false;
      }
      // Show only if current operation type is in the allowed list
      return partyConfig.operationTypes.includes(currentOperationType);
    })
    .map(([key, partyConfig]) => ({
      id: key,
      title: partyConfig.title,
      description: `${partyConfig.title} ${
        type === "income" ? "receivables" : "payables"
      }`,
      value: key,
      financialType: type === "income" ? "Receivables" : "Payables",
    }));
};

const PartySummaryCard: React.FC<{
  partyType: string;
  summary: PartySummary;
  type: "income" | "expense";
}> = ({ partyType, summary, type }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  let config;
  if (type === "income") {
    config =
      incomePartyTypeConfig[partyType as keyof typeof incomePartyTypeConfig];
  } else {
    config =
      expensePartyTypeConfig[partyType as keyof typeof expensePartyTypeConfig];
  }

  if (!config) return null;

  const handleCardClick = () => {
    // Show coming soon toast for client party type
    if (type === "income") {
      navigate(`/finance/income/party/${partyType}`);
    } else {
      navigate(`/finance/expense/party/${partyType}`);
    }
  };

  const isIncome = type === "income";

  return (
    <Card
      sx={{
        height: "100%",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: 3,
          cursor: "pointer",
        },
        borderRadius: 2,
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
        {/* Header with counts on the right */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Box
            sx={{
              backgroundColor: "primary.main",
              color: "white",
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
              flexShrink: 0,
            }}
          >
            {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ fontSize: "0.8rem" }}
            >
              {config.title}
            </Typography>
          </Box>
          {/* Summary Stats moved to the right */}
          <Box
            sx={{
              textAlign: "center",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ fontSize: "0.85rem" }}
            >
              {formatCurrency(summary.totalBalance)}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.65rem" }}
            >
              Balance
            </Typography>
          </Box>
        </Box>

        {/* Financial Details */}
        <Box sx={{ mb: 0.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.25,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              Parties:
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ fontSize: "0.75rem" }}
            >
              {summary.totalParties}
            </Typography>
          </Box>
          {isIncome ? (
            // Income-specific financial details
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  Booking:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {formatCurrency(summary.totalBooking || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  Receivable:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {formatCurrency(summary.netReceivable || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  Received:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {formatCurrency(summary.netReceived || 0)}
                </Typography>
              </Box>
            </>
          ) : (
            // Expense-specific financial details
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  Payable:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {formatCurrency(summary.totalDue || 0)}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                  Paid:
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "0.75rem" }}
                >
                  {formatCurrency(summary.totalPaid || 0)}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const PartySummaryCards: React.FC<PartySummaryCardsProps> = ({
  summaryData,
  loading,
  type = "expense",
}) => {
  const { planStatus } = usePlanStatus();

  // Get the appropriate configuration based on type
  const partyTypeConfig =
    type === "income" ? incomePartyTypeConfig : expensePartyTypeConfig;

  // Filter party types based on current user's operation type
  const relevantPartyTypes = Object.keys(partyTypeConfig).filter(
    (partyType) => {
      const config = partyTypeConfig[partyType as keyof typeof partyTypeConfig];
      // If no operationTypes defined, show for all operations (backward compatibility)
      if (!config.operationTypes || config.operationTypes.length === 0) {
        return true;
      }
      // Show only if current operation type is in the allowed list
      return config.operationTypes.includes(planStatus?.operationType);
    }
  );

  if (loading) {
    return (
      <Grid container spacing={2}>
        {relevantPartyTypes?.map((partyType) => (
          <Grid item xs={12} sm={6} md={4} key={partyType}>
            <Card sx={{ height: 250 }}>
              <CardContent>
                <Typography>Loading...</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      {relevantPartyTypes?.map((partyType) => {
        // Get summary data for this party type, or create default empty summary
        const summary = summaryData[partyType] || {
          partyType,
          totalParties: 0,
          totalTransactions: 0,
          totalDue: 0,
          totalPaid: 0,
          totalBalance: 0,
          totalSettlement: 0,
          totalBooking: 0,
          totalCollection: 0,
          totalAdvance: 0,
          netReceived: 0,
          netReceivable: 0,
        };

        return (
          <Grid item xs={12} sm={6} md={4} key={partyType}>
            <PartySummaryCard
              partyType={partyType}
              summary={summary}
              type={type}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

export default PartySummaryCards;
